import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { 
  Clock, 
  CheckCircle, 
  AlertTriangle,
  Calendar,
  CalendarCheck,
  Phone,
  Search,
  X,
  Minus,
  Plus,
  Settings
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ControleRetornoPage({ user, onLogout, showConfig }) {
  const [loading, setLoading] = useState(true);
  const [relatorio, setRelatorio] = useState(null);
  const [filtrosAtivos, setFiltrosAtivos] = useState(['todos']);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedConsulta, setSelectedConsulta] = useState(null);
  const [retornosConfig, setRetornosConfig] = useState(1);
  const [savingRetornos, setSavingRetornos] = useState(false);
  const [cicloTimeline, setCicloTimeline] = useState(null);
  const [loadingCiclo, setLoadingCiclo] = useState(false);
  
  // Config modal state
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showConfirmRemover, setShowConfirmRemover] = useState(false);
  const [procedimentos, setProcedimentos] = useState([]);
  const [prazoGlobal, setPrazoGlobal] = useState(40);
  const [savingConfig, setSavingConfig] = useState(false);

  useEffect(() => {
    fetchRelatorio();
    loadConfigRetorno();
  }, []);

  const fetchRelatorio = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/relatorios/retornos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRelatorio(response.data);
    } catch (error) {
      toast.error('Erro ao carregar relatório de retornos');
    } finally {
      setLoading(false);
    }
  };

  const loadConfigRetorno = async () => {
    try {
      const token = localStorage.getItem('token');
      const [procResp, configResp] = await Promise.all([
        axios.get(`${API}/procedimentos`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/configuracoes-medico`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setProcedimentos(procResp.data);
      setPrazoGlobal(configResp.data.prazo_retorno_dias_global ?? 40);
    } catch {
      toast.error('Erro ao carregar configurações');
    }
  };

  const handleOpenConfig = () => {
    loadConfigRetorno();
    setShowConfigModal(true);
  };

  const handleSavePrazoGlobal = async (valor) => {
    setSavingConfig(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/configuracoes-medico`, 
        { prazo_retorno_dias_global: valor },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPrazoGlobal(valor);
      toast.success(`Prazo de retorno atualizado para ${valor} dias`);
      // Reload data to reflect updated statuses
      fetchRelatorio();
    } catch {
      toast.error('Erro ao salvar prazo');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleSaveRetornosPadrao = async (procedimentoId, novoValor) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/procedimentos/${procedimentoId}/retornos-padrao`, 
        { retornos_padrao: novoValor },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProcedimentos(prev => prev.map(p => 
        p.id === procedimentoId ? { ...p, retornos_padrao: novoValor } : p
      ));
      toast.success('Retornos padrão atualizado');
    } catch {
      toast.error('Erro ao salvar');
    }
  };

  const getRetornosPadraoProcedimento = (categoria) => {
    const proc = procedimentos.find(p => p.nome === categoria);
    return proc?.retornos_padrao ?? 1;
  };

  const isRetornoPersonalizado = (consulta) => {
    if (!consulta || procedimentos.length === 0) return false;
    const padrao = getRetornosPadraoProcedimento(consulta.categoria);
    const concedidos = consulta.retornos_concedidos ?? 1;
    return concedidos !== padrao;
  };


  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const [year, month, day] = dateStr.split('-');
      return `${day}/${month}/${year}`;
    } catch {
      return dateStr;
    }
  };

  const calcularDiasRestantes = (dataLimite) => {
    if (!dataLimite) return null;
    try {
      const limite = new Date(dataLimite);
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      limite.setHours(0, 0, 0, 0);
      return Math.ceil((limite - hoje) / (1000 * 60 * 60 * 24));
    } catch {
      return null;
    }
  };

  const getStatusRetorno = (consulta) => {
    // Quando a consulta de retorno em si é a entrada (já realizada como retorno gratuito)
    if (consulta.tipo_cobranca === 'retorno_gratuito' && consulta.status === 'realizada') {
      return { status: 'utilizado', label: 'Utilizado', color: 'blue' };
    }

    const retornosConcedidos = consulta.retornos_concedidos ?? 1;
    const retornosUtilizados = consulta.retornos_utilizados ?? 0;

    if (retornosConcedidos === 0) {
      return { status: 'sem_retorno', label: 'Sem retorno', color: 'gray' };
    }

    // Se a origem tem retorno marcado mas ainda não realizado → Agendado
    if (consulta.retorno_agendado_data && retornosUtilizados >= retornosConcedidos) {
      return { status: 'agendado', label: 'Agendado', color: 'amber' };
    }

    if (retornosUtilizados >= retornosConcedidos) {
      return { status: 'utilizado', label: 'Utilizado', color: 'blue' };
    }

    const dias = calcularDiasRestantes(consulta.data_limite_retorno);
    if (dias === null) return { status: 'indeterminado', label: 'Indeterminado', color: 'gray' };

    if (dias < 0) return { status: 'vencido', label: `Vencido`, color: 'red' };
    if (dias === 0) return { status: 'vencendo', label: 'Vencendo', color: 'yellow' };
    if (dias <= 15) return { status: 'vencendo', label: 'Vencendo', color: 'yellow' };

    return { status: 'disponivel', label: `${dias} dias`, color: 'green' };
  };

  const fetchCicloRetornos = async (consultaId) => {
    setLoadingCiclo(true);
    setCicloTimeline(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/consultas/${consultaId}/ciclo-retornos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCicloTimeline(response.data.timeline);
    } catch {
      setCicloTimeline(null);
    } finally {
      setLoadingCiclo(false);
    }
  };

  const handleSelectConsulta = useCallback((consulta) => {
    // Clicking same item deselects and closes panel
    if (selectedConsulta?.id === consulta.id) {
      setSelectedConsulta(null);
      setCicloTimeline(null);
      return;
    }
    setSelectedConsulta(consulta);
    setRetornosConfig(consulta.retornos_concedidos ?? 1);
    fetchCicloRetornos(consulta.id);
  }, [selectedConsulta]);

  const handleClosePanel = useCallback(() => {
    setSelectedConsulta(null);
    setCicloTimeline(null);
  }, []);

  const handleSaveRetornos = async (novoValor) => {
    if (!selectedConsulta) return;
    setSavingRetornos(true);
    try {
      const token = localStorage.getItem('token');
      if (selectedConsulta.status === 'realizada') {
        const response = await axios.put(
          `${API}/consultas/${selectedConsulta.id}/retornos-config?retornos_concedidos=${novoValor}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSelectedConsulta(response.data.consulta);
      } else {
        await axios.put(
          `${API}/consultas/${selectedConsulta.id}`,
          { retornos_concedidos: novoValor },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSelectedConsulta(prev => ({ ...prev, retornos_concedidos: novoValor }));
      }
      setRetornosConfig(novoValor);
      toast.success(novoValor === 0 ? 'Retorno removido' : `${novoValor} retorno(s) configurado(s)`);
      fetchRelatorio();
      if (selectedConsulta) fetchCicloRetornos(selectedConsulta.id);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao salvar';
      toast.error(msg);
    } finally {
      setSavingRetornos(false);
    }
  };

  const toggleFiltro = (filtro) => {
    if (filtro === 'todos') {
      setFiltrosAtivos(['todos']);
      return;
    }
    setFiltrosAtivos(prev => {
      const semTodos = prev.filter(f => f !== 'todos');
      const novo = semTodos.includes(filtro)
        ? semTodos.filter(f => f !== filtro)
        : [...semTodos, filtro];
      return novo.length === 0 ? ['todos'] : novo;
    });
  };

  const getAllDisponiveisComRetorno = () => {
    if (!relatorio) return [];
    const all = [...(relatorio.disponiveis || []), ...(relatorio.vencidos || [])];
    const seen = new Set();
    return all.filter(c => {
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });
  };

  const countAgendados = () => {
    return (relatorio?.agendados || []).length;
  };

  const countNaoAgendados = () => {
    const todos = getAllDisponiveisComRetorno();
    return todos.filter(c => !c.retorno_agendado_data).length;
  };

  const getConsultasFiltradas = () => {
    if (!relatorio) return [];

    const usaTodos = filtrosAtivos.includes('todos');
    let consultas = [];

    const todosDisponiveis = getAllDisponiveisComRetorno();

    if (usaTodos || filtrosAtivos.includes('disponiveis')) {
      consultas.push(...todosDisponiveis);
    }
    if (usaTodos || filtrosAtivos.includes('agendados')) {
      consultas.push(...(relatorio.agendados || []));
    }
    if (filtrosAtivos.includes('nao_agendados') && !usaTodos) {
      const naoAgendados = todosDisponiveis.filter(c => !c.retorno_agendado_data);
      consultas.push(...naoAgendados);
    }
    if (usaTodos || filtrosAtivos.includes('utilizados')) {
      consultas.push(...(relatorio.utilizados || []));
    }

    const seen = new Set();
    consultas = consultas.filter(c => {
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      consultas = consultas.filter(c =>
        c.paciente_nome?.toLowerCase().includes(term) ||
        c.categoria?.toLowerCase().includes(term)
      );
    }

    return consultas.sort((a, b) => (a.paciente_nome || '').localeCompare(b.paciente_nome || '', 'pt-BR'));
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} showConfig={showConfig}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </Layout>
    );
  }

  const consultasFiltradas = getConsultasFiltradas();
  const totalDisponiveis = getAllDisponiveisComRetorno().length;
  const totalAgendados = countAgendados();
  const totalNaoAgendados = countNaoAgendados();
  const totalUtilizados = (relatorio?.utilizados || []).length;

  const filtros = [
    { key: 'todos', label: 'Todos' },
    { key: 'disponiveis', label: 'Disponível' },
    { key: 'agendados', label: 'Agendado' },
    { key: 'nao_agendados', label: 'Não Agendado' },
    { key: 'utilizados', label: 'Utilizado' },
  ];

  const panelDias = selectedConsulta ? calcularDiasRestantes(selectedConsulta.data_limite_retorno) : null;
  const panelUtilizados = selectedConsulta ? (selectedConsulta.retornos_utilizados ?? 0) : 0;
  const panelRestantes = retornosConfig - panelUtilizados;

  return (
    <Layout user={user} onLogout={onLogout} showConfig={showConfig}>
      <div className="flex relative" data-testid="controle-retorno-page">
        {/* Main Content */}
        <div className={`flex-1 transition-all duration-300 ${selectedConsulta ? 'sm:mr-[380px]' : ''}`}>
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6 flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Controle de Retorno</h1>
                <p className="text-gray-600 mt-1">Gerencie os retornos disponíveis dos pacientes</p>
              </div>
              <Button
                variant="outline"
                onClick={handleOpenConfig}
                className="flex items-center gap-2 text-gray-700"
                data-testid="btn-config-retorno"
              >
                <Settings className="w-4 h-4" />
                <span className="hidden sm:inline">Configuração Retorno</span>
                <span className="sm:hidden">Config</span>
              </Button>
            </div>

            {/* Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-xl p-4 sm:p-5 border-2 border-gray-100 text-left" data-testid="card-disponiveis">
                <p className="text-2xl sm:text-3xl font-bold text-gray-900">{totalDisponiveis}</p>
                <p className="text-sm text-gray-600 mt-1">Disponível</p>
              </div>

              <div className="bg-white rounded-xl p-4 sm:p-5 border-2 border-gray-100 text-left" data-testid="card-agendados">
                <p className="text-2xl sm:text-3xl font-bold text-gray-900">{totalAgendados}</p>
                <p className="text-sm text-gray-600 mt-1">Agendado</p>
              </div>

              <div className="bg-white rounded-xl p-4 sm:p-5 border-2 border-gray-100 text-left" data-testid="card-nao-agendados">
                <p className="text-2xl sm:text-3xl font-bold text-gray-900">{totalNaoAgendados}</p>
                <p className="text-sm text-gray-600 mt-1">Não Agendado</p>
              </div>

              <div className="bg-white rounded-xl p-4 sm:p-5 border-2 border-gray-100 text-left" data-testid="card-utilizados">
                <p className="text-2xl sm:text-3xl font-bold text-gray-900">{totalUtilizados}</p>
                <p className="text-sm text-gray-600 mt-1">Utilizado</p>
              </div>
            </div>

            {/* Filtros checkbox + Busca - INALTERADO */}
            <div className="bg-white rounded-xl border border-gray-100 p-4 mb-6">
              <div className="flex flex-col gap-4">
                <div className="flex flex-wrap items-center gap-3" data-testid="filtros-checkbox">
                  {filtros.map(f => (
                    <label
                      key={f.key}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer transition-colors text-sm ${
                        filtrosAtivos.includes(f.key)
                          ? 'bg-blue-50 border-blue-300 text-blue-700'
                          : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={filtrosAtivos.includes(f.key)}
                        onChange={() => toggleFiltro(f.key)}
                        className="w-3.5 h-3.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      {f.label}
                    </label>
                  ))}
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar por paciente ou procedimento..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                    data-testid="busca-retorno"
                  />
                </div>
              </div>
            </div>

            {/* Tabela */}
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden" data-testid="lista-retornos">
              <div className={`${selectedConsulta ? 'hidden' : 'hidden sm:grid'} gap-3 px-6 py-3 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wider`} style={{gridTemplateColumns: 'minmax(0,2fr) minmax(0,1.5fr) minmax(0,0.8fr) minmax(0,1.4fr) minmax(0,1fr) minmax(0,0.9fr)'}}>
                <div>Paciente</div>
                <div>Procedimento</div>
                <div>Retornos</div>
                <div>Próximo Retorno</div>
                <div>Limite</div>
                <div>Status</div>
              </div>

              <div className="divide-y divide-gray-100">
                {consultasFiltradas.length === 0 ? (
                  <div className="px-6 py-12 text-center" data-testid="lista-vazia">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Calendar className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500">Nenhum retorno encontrado</p>
                  </div>
                ) : (
                  consultasFiltradas.map((consulta) => {
                    const statusRetorno = getStatusRetorno(consulta);
                    const retornoAgendado = consulta.retorno_agendado_data;
                    const isSelected = selectedConsulta?.id === consulta.id;
                    const dias = calcularDiasRestantes(consulta.data_limite_retorno);
                    const isVencido = dias !== null && dias < 0;
                    const isUtilizado = statusRetorno.status === 'utilizado';
                    const isForaDoPrazo = retornoAgendado && isVencido && !isUtilizado;

                    return (
                      <div
                        key={consulta.id}
                        onClick={() => handleSelectConsulta(consulta)}
                        className={`px-4 sm:px-6 py-3 cursor-pointer transition-colors ${
                          isSelected ? 'bg-blue-100 border-l-[6px] border-l-blue-600 ring-1 ring-inset ring-blue-200' : 'hover:bg-gray-50 border-l-[6px] border-l-transparent'
                        }`}
                        data-testid={`retorno-item-${consulta.id}`}
                      >
                        {/* Mobile / Compact (when side panel is open) */}
                        <div className={selectedConsulta ? 'block' : 'sm:hidden'}>
                          <div className="flex items-start justify-between mb-2">
                            <div className="min-w-0 flex-1">
                              <h3 className="font-medium text-blue-700 underline decoration-blue-300 underline-offset-2">{consulta.paciente_nome}</h3>
                              <p className="text-sm text-gray-500">{consulta.categoria}</p>
                            </div>
                            <span className={`ml-2 flex-shrink-0 px-2 py-1 rounded-full text-xs font-medium ${
                              statusRetorno.color === 'green' ? 'bg-green-100 text-green-700' :
                              statusRetorno.color === 'yellow' ? 'bg-yellow-100 text-yellow-700' :
                              statusRetorno.color === 'red' ? 'bg-red-100 text-red-700' :
                              statusRetorno.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {statusRetorno.label}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              Retornos: {consulta.retornos_utilizados ?? 0}/{consulta.retornos_concedidos ?? 1}
                              {isRetornoPersonalizado(consulta) && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-300">Personalizado</span>
                              )}
                            </span>
                            {consulta.data_limite_retorno && (
                              <span className={isVencido ? 'text-red-600 font-medium' : ''}>
                                Limite: {formatDate(consulta.data_limite_retorno)}
                              </span>
                            )}
                            {isUtilizado ? (
                              <span className="text-gray-300">---</span>
                            ) : retornoAgendado ? (
                              <span className="flex items-center gap-1">
                                <span className="text-purple-600 font-medium">{formatDate(retornoAgendado)}</span>
                                {isForaDoPrazo && (
                                  <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-300">Fora do prazo</span>
                                )}
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-500 border border-dashed border-gray-300">Não agendado</span>
                            )}
                          </div>
                        </div>

                        {/* Desktop (only when side panel is closed) */}
                        <div className={`${selectedConsulta ? 'hidden' : 'hidden sm:grid'} gap-3 items-center min-h-[56px]`} style={{gridTemplateColumns: 'minmax(0,2fr) minmax(0,1.5fr) minmax(0,0.8fr) minmax(0,1.4fr) minmax(0,1fr) minmax(0,0.9fr)'}}>
                          <div className="min-w-0">
                            <h3 className="font-medium text-blue-700 truncate underline decoration-blue-300 underline-offset-2">{consulta.paciente_nome}</h3>
                          </div>

                          <div className="min-w-0">
                            <p className="text-sm text-gray-900 truncate">{consulta.categoria}</p>
                          </div>

                          <div className="min-w-0 flex flex-col gap-1 items-start">
                            <span className="text-sm font-medium text-gray-700 tabular-nums">
                              {consulta.retornos_utilizados ?? 0}/{consulta.retornos_concedidos ?? 1}
                            </span>
                            {isRetornoPersonalizado(consulta) && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-300" data-testid="badge-personalizado">
                                Personalizado
                              </span>
                            )}
                          </div>

                          <div className="min-w-0 flex flex-col gap-1 items-start">
                            {isUtilizado ? (
                              <span className="text-gray-300 text-sm">---</span>
                            ) : retornoAgendado ? (
                              <>
                                <span className="text-sm font-medium text-purple-700 tabular-nums">{formatDate(retornoAgendado)}</span>
                                {isForaDoPrazo && (
                                  <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-300" data-testid="badge-fora-prazo">Fora do prazo</span>
                                )}
                              </>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-500 border border-dashed border-gray-300" data-testid="nao-agendado">Não agendado</span>
                            )}
                          </div>

                          <div className="min-w-0">
                            <p className={`text-sm tabular-nums ${isVencido ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                              {consulta.data_limite_retorno ? formatDate(consulta.data_limite_retorno) : '-'}
                            </p>
                          </div>

                          <div className="min-w-0">
                            <span className={`inline-flex whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-medium ${
                              statusRetorno.color === 'green' ? 'bg-green-100 text-green-700' :
                              statusRetorno.color === 'yellow' ? 'bg-yellow-100 text-yellow-700' :
                              statusRetorno.color === 'red' ? 'bg-red-100 text-red-700' :
                              statusRetorno.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {statusRetorno.label}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                Clique em uma linha para ajustar a quantidade de retornos. Use <strong>"Configuração Retorno"</strong> para definir os padrões.
              </p>
            </div>
          </div>
        </div>

        {/* Side Panel - Desktop */}
        {selectedConsulta && (
          <>
            {/* Mobile overlay (only when panel is full-screen modal) */}
            <div 
              className="fixed inset-0 bg-black/40 z-40 sm:hidden"
              onClick={handleClosePanel}
              data-testid="panel-overlay"
            />

            {/* Panel */}
            <div 
              className="fixed inset-y-0 right-0 w-full sm:w-[380px] bg-white border-l border-gray-200 z-50 sm:z-10 overflow-y-auto shadow-xl sm:shadow-md"
              data-testid="side-panel"
            >
              <div className="p-5">
                {/* Header */}
                <div className="flex items-center justify-between mb-5 pb-4 border-b border-gray-200">
                  <h2 className="text-base font-bold text-gray-900">Detalhes do Retorno</h2>
                  <button 
                    onClick={handleClosePanel}
                    className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
                    data-testid="panel-close-btn"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Paciente Info */}
                <div className="mb-5">
                  <h3 className="text-lg font-semibold text-gray-900">{selectedConsulta.paciente_nome}</h3>
                  <p className="text-sm text-gray-500">{selectedConsulta.categoria}</p>
                </div>

                {/* Alerta Fora do Prazo */}
                {(() => {
                  const panelIsVencido = panelDias !== null && panelDias < 0;
                  const panelRetornoAgendado = selectedConsulta.retorno_agendado_data;
                  const panelIsUtilizado = (selectedConsulta.retornos_utilizados ?? 0) >= (selectedConsulta.retornos_concedidos ?? 1);
                  if (panelIsVencido && panelRetornoAgendado && !panelIsUtilizado) {
                    return (
                      <div className="mb-5 bg-red-50 border border-red-200 rounded-xl p-3" data-testid="panel-alert-fora-prazo">
                        <div className="flex gap-2">
                          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-red-700 leading-relaxed">
                            O prazo recomendado para retorno <strong>venceu em {formatDate(selectedConsulta.data_limite_retorno)}</strong>. O próximo retorno está agendado para <strong>{formatDate(panelRetornoAgendado)}</strong>, fora do prazo de recomendação.
                          </p>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Info Grid */}
                <div className="mb-5">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Informações</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-400 mb-0.5">Primeira Consulta</p>
                      <p className="text-sm font-semibold text-gray-800">{formatDate(selectedConsulta.data_consulta)}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-400 mb-0.5">Limite</p>
                      <p className={`text-sm font-semibold ${panelDias !== null && panelDias < 0 ? 'text-red-600' : 'text-gray-800'}`}>
                        {selectedConsulta.data_limite_retorno ? formatDate(selectedConsulta.data_limite_retorno) : '-'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Retornos Concedidos Control */}
                <div className="mb-5">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Retornos Concedidos</p>
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 text-center">
                    <div className="flex items-center justify-center gap-5">
                      <button
                        onClick={() => {
                          const novo = Math.max(1, retornosConfig - 1);
                          setRetornosConfig(novo);
                          handleSaveRetornos(novo);
                        }}
                        disabled={savingRetornos || retornosConfig <= 1}
                        className="w-10 h-10 rounded-lg border-2 border-blue-300 bg-white text-blue-600 flex items-center justify-center hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        data-testid="panel-btn-minus"
                      >
                        <Minus className="w-5 h-5" />
                      </button>
                      <span className="text-4xl font-extrabold text-blue-800" data-testid="panel-retornos-value">
                        {retornosConfig}
                      </span>
                      <button
                        onClick={() => {
                          const novo = retornosConfig + 1;
                          setRetornosConfig(novo);
                          handleSaveRetornos(novo);
                        }}
                        disabled={savingRetornos}
                        className="w-10 h-10 rounded-lg border-2 border-blue-300 bg-white text-blue-600 flex items-center justify-center hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        data-testid="panel-btn-plus"
                      >
                        <Plus className="w-5 h-5" />
                      </button>
                    </div>
                    <p className="mt-2 text-sm text-blue-600">
                      {retornosConfig === 0 ? 'Sem direito a retorno' : `${panelUtilizados} de ${retornosConfig} utilizado(s)`}
                    </p>
                    {isRetornoPersonalizado(selectedConsulta) && (
                      <span className="inline-flex items-center mt-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-300" data-testid="panel-badge-personalizado">
                        Personalizado
                      </span>
                    )}
                  </div>
                </div>

                {/* Timeline do Ciclo */}
                <div className="mb-5">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Ciclo de Retornos</p>
                  {loadingCiclo ? (
                    <div className="flex justify-center py-4">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                    </div>
                  ) : cicloTimeline && cicloTimeline.length > 0 ? (
                    <div className="relative" data-testid="ciclo-timeline">
                      {/* Vertical line */}
                      <div className="absolute left-[15px] top-3 bottom-3 w-px bg-gray-200" />

                      <div className="space-y-0">
                        {cicloTimeline.map((item, idx) => {
                          const isFirst = item.tipo === 'primeira_consulta';
                          const isRealizada = item.status === 'realizada';
                          const isAgendada = item.status === 'agendada' || item.status === 'confirmada';
                          const isPendente = item.status === 'pendente';

                          let dotColor = 'bg-gray-300';
                          let ringColor = 'ring-gray-200';
                          let textColor = 'text-gray-400';
                          let statusLabel = '';

                          if (isFirst && isRealizada) {
                            dotColor = 'bg-emerald-500';
                            ringColor = 'ring-emerald-100';
                            textColor = 'text-gray-900';
                            statusLabel = 'Realizada';
                          } else if (isRealizada) {
                            dotColor = 'bg-blue-500';
                            ringColor = 'ring-blue-100';
                            textColor = 'text-gray-900';
                            statusLabel = 'Realizado';
                          } else if (isAgendada) {
                            dotColor = 'bg-purple-500';
                            ringColor = 'ring-purple-100';
                            textColor = 'text-gray-700';
                            statusLabel = 'Agendado';
                          } else if (isPendente) {
                            dotColor = 'bg-gray-300';
                            ringColor = 'ring-gray-100';
                            textColor = 'text-gray-400';
                            statusLabel = 'Pendente';
                          }

                          return (
                            <div key={idx} className="relative flex items-start gap-3 py-2.5 pl-0">
                              {/* Dot */}
                              <div className={`relative z-10 w-[30px] h-[30px] flex-shrink-0 rounded-full ${dotColor} ring-4 ${ringColor} flex items-center justify-center`}>
                                {isFirst ? (
                                  <Calendar className="w-3.5 h-3.5 text-white" />
                                ) : isRealizada ? (
                                  <CheckCircle className="w-3.5 h-3.5 text-white" />
                                ) : isAgendada ? (
                                  <CalendarCheck className="w-3.5 h-3.5 text-white" />
                                ) : (
                                  <Clock className="w-3.5 h-3.5 text-white" />
                                )}
                              </div>

                              {/* Content */}
                              <div className="flex-1 min-w-0 pt-0.5">
                                <div className="flex items-center justify-between">
                                  <p className={`text-sm font-medium ${textColor}`}>{item.label}</p>
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                    isFirst && isRealizada ? 'bg-emerald-100 text-emerald-700' :
                                    isRealizada ? 'bg-blue-100 text-blue-700' :
                                    isAgendada ? 'bg-purple-100 text-purple-700' :
                                    'bg-gray-100 text-gray-500'
                                  }`}>
                                    {statusLabel}
                                  </span>
                                </div>
                                <p className={`text-sm ${item.data ? 'text-gray-600' : 'text-gray-400 italic'}`}>
                                  {item.data ? formatDate(item.data) : 'Nao agendado'}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-sm text-gray-400">Nenhum dado do ciclo disponivel</p>
                    </div>
                  )}
                </div>

                {/* Action: Remover todos retornos */}
                <button
                  onClick={() => setShowConfirmRemover(true)}
                  disabled={savingRetornos || retornosConfig === 0}
                  className="w-full py-2.5 rounded-lg border border-red-300 text-red-600 text-sm font-medium hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="panel-btn-remove-all"
                >
                  Remover Todos os Retornos
                </button>

                <button
                  onClick={handleClosePanel}
                  className="w-full py-2.5 rounded-lg border border-gray-300 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors mt-2"
                  data-testid="panel-btn-fechar"
                >
                  Fechar
                </button>
              </div>
            </div>
          </>
        )}

        {/* Modal Confirmar Remoção de Retornos */}
        {showConfirmRemover && (
          <>
            <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowConfirmRemover(false)} />
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-7 h-7 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5Z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Remover todos os retornos?
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Tem certeza que deseja remover todos os retornos de <strong>{selectedConsulta?.paciente_nome}</strong>? 
                    Esta ação zerará os retornos concedidos para esta consulta.
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowConfirmRemover(false);
                      setRetornosConfig(0);
                      handleSaveRetornos(0);
                    }}
                    className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
                    data-testid="modal-confirmar-remover"
                  >
                    Remover
                  </button>
                  <button
                    onClick={() => setShowConfirmRemover(false)}
                    className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
                    data-testid="modal-cancelar-remover"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Modal Configuração Retorno */}
        {showConfigModal && (
          <>
            <div className="fixed inset-0 bg-black/40 z-50" onClick={() => setShowConfigModal(false)} />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowConfigModal(false)}>
              <div 
                className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-gray-200">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Configuração Retorno</h2>
                    <p className="text-sm text-gray-500 mt-0.5">Defina o prazo e a quantidade de retornos por procedimento</p>
                  </div>
                  <button 
                    onClick={() => setShowConfigModal(false)}
                    className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-100"
                    data-testid="config-modal-close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="p-5 space-y-6">
                  {/* Prazo Global */}
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Prazo de Retorno (Global)</p>
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <p className="text-sm text-gray-700 mb-3">Quantidade de dias que o paciente tem para usar o retorno após a consulta realizada.</p>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => { const v = Math.max(1, prazoGlobal - 5); setPrazoGlobal(v); handleSavePrazoGlobal(v); }}
                          disabled={savingConfig || prazoGlobal <= 1}
                          className="w-9 h-9 rounded-lg border border-blue-300 bg-white text-blue-600 flex items-center justify-center hover:bg-blue-100 disabled:opacity-40 transition-colors"
                          data-testid="config-prazo-minus"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <input
                          type="number"
                          min="1"
                          value={prazoGlobal}
                          onChange={(e) => setPrazoGlobal(Math.max(1, parseInt(e.target.value) || 1))}
                          onBlur={() => handleSavePrazoGlobal(prazoGlobal)}
                          className="w-20 text-center text-2xl font-bold text-blue-800 border border-blue-200 rounded-lg py-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                          data-testid="config-prazo-input"
                        />
                        <button
                          onClick={() => { const v = prazoGlobal + 5; setPrazoGlobal(v); handleSavePrazoGlobal(v); }}
                          disabled={savingConfig}
                          className="w-9 h-9 rounded-lg border border-blue-300 bg-white text-blue-600 flex items-center justify-center hover:bg-blue-100 disabled:opacity-40 transition-colors"
                          data-testid="config-prazo-plus"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                        <span className="text-sm text-blue-600 font-medium">dias</span>
                      </div>
                    </div>
                  </div>

                  {/* Retornos por Procedimento */}
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Retornos Padrão por Procedimento</p>
                    <p className="text-sm text-gray-500 mb-3">Defina quantos retornos cada tipo de procedimento concede automaticamente ao ser marcado como realizado.</p>
                    <div className="space-y-2">
                      {procedimentos.filter(p => p.ativo !== false).map(proc => (
                        <div 
                          key={proc.id} 
                          className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3 border border-gray-100"
                          data-testid={`config-proc-${proc.id}`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: proc.cor }} />
                            <span className="text-sm font-medium text-gray-800 truncate">{proc.nome}</span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={() => handleSaveRetornosPadrao(proc.id, Math.max(0, (proc.retornos_padrao ?? 1) - 1))}
                              disabled={(proc.retornos_padrao ?? 1) <= 0}
                              className="w-7 h-7 rounded border border-gray-300 bg-white text-gray-600 flex items-center justify-center hover:bg-gray-100 disabled:opacity-40 transition-colors"
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="w-8 text-center text-lg font-bold text-gray-800">
                              {proc.retornos_padrao ?? 1}
                            </span>
                            <button
                              onClick={() => handleSaveRetornosPadrao(proc.id, (proc.retornos_padrao ?? 1) + 1)}
                              className="w-7 h-7 rounded border border-gray-300 bg-white text-gray-600 flex items-center justify-center hover:bg-gray-100 transition-colors"
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      ))}
                      {procedimentos.length === 0 && (
                        <p className="text-sm text-gray-400 text-center py-4">Nenhum procedimento cadastrado</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="p-5 border-t border-gray-200">
                  <Button
                    onClick={() => setShowConfigModal(false)}
                    className="w-full"
                    data-testid="config-modal-done"
                  >
                    Concluído
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
